/**
 * Token Service - Secure token storage and validation
 * 
 * Provides centralized token management with:
 * - JWT parsing and validation
 * - Token expiry detection
 * - Secure storage abstraction (localStorage/sessionStorage)
 * - Refresh token management
 */

const TOKEN_KEY = 'session_token'
const REFRESH_KEY = 'refresh_token'
const TOKEN_STORAGE_TYPE_KEY = 'token_storage_type'

/**
 * Parse JWT token payload without verification
 * @param {string} token - JWT token
 * @returns {Object|null} Decoded payload or null if invalid
 */
function parseJwt(token) {
  try {
    if (!token || typeof token !== 'string') return null
    const parts = token.split('.')
    if (parts.length !== 3) return null
    
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(json)
  } catch (e) {
    console.error('[TokenService] Failed to parse JWT:', e)
    return null
  }
}

/**
 * Get the appropriate storage based on current settings
 * @returns {Storage} localStorage or sessionStorage
 */
function getStorage() {
  const storageType = localStorage.getItem(TOKEN_STORAGE_TYPE_KEY) || 'local'
  return storageType === 'session' ? sessionStorage : localStorage
}

export const tokenService = {
  /**
   * Get the current access token
   * @returns {string|null} The access token or null if not found
   */
  getToken() {
    // Check sessionStorage first (more secure), then localStorage
    return sessionStorage.getItem(TOKEN_KEY) || 
           localStorage.getItem(TOKEN_KEY) || 
           null
  },

  /**
   * Get the refresh token (always from localStorage for persistence)
   * @returns {string|null} The refresh token or null if not found
   */
  getRefreshToken() {
    return localStorage.getItem(REFRESH_KEY)
  },

  /**
   * Store the access token
   * @param {string} token - The access token
   * @param {boolean} useSessionStorage - If true, store in sessionStorage (more secure, cleared on tab close)
   */
  setToken(token, useSessionStorage = false) {
    if (!token || typeof token !== 'string') {
      console.error('[TokenService] Invalid token provided')
      return
    }
    
    const storage = useSessionStorage ? sessionStorage : localStorage
    const storageType = useSessionStorage ? 'session' : 'local'
    
    storage.setItem(TOKEN_KEY, token)
    localStorage.setItem(TOKEN_STORAGE_TYPE_KEY, storageType)
    
    console.log(`[TokenService] Token stored in ${storageType}Storage`)
  },

  /**
   * Store the refresh token (always in localStorage)
   * @param {string} token - The refresh token
   */
  setRefreshToken(token) {
    if (!token || typeof token !== 'string') {
      console.error('[TokenService] Invalid refresh token provided')
      return
    }
    localStorage.setItem(REFRESH_KEY, token)
  },

  /**
   * Clear all tokens from storage
   */
  clearTokens() {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(REFRESH_KEY)
    localStorage.removeItem(TOKEN_STORAGE_TYPE_KEY)
    sessionStorage.removeItem(TOKEN_KEY)
    console.log('[TokenService] All tokens cleared')
  },

  /**
   * Check if the token is valid (exists and not expired)
   * @param {string} [token] - Optional token to check, defaults to stored token
   * @returns {boolean} True if token is valid
   */
  isTokenValid(token = this.getToken()) {
    if (!token) return false
    
    const payload = parseJwt(token)
    if (!payload) return false
    
    // If no exp claim, assume it's an opaque token and treat as valid
    if (!payload.exp) return true
    
    const now = Math.floor(Date.now() / 1000)
    // Consider token expired 60 seconds early to account for clock skew and network latency
    return payload.exp > now + 60
  },

  /**
   * Check if token is expiring soon
   * @param {number} thresholdSeconds - Threshold in seconds (default: 5 minutes)
   * @returns {boolean} True if token expires within threshold
   */
  isTokenExpiringSoon(thresholdSeconds = 300) {
    const token = this.getToken()
    if (!token) return false
    
    const payload = parseJwt(token)
    if (!payload?.exp) return false
    
    const now = Math.floor(Date.now() / 1000)
    const timeUntilExpiry = payload.exp - now
    
    return timeUntilExpiry > 0 && timeUntilExpiry < thresholdSeconds
  },

  /**
   * Get token expiry timestamp
   * @returns {number|null} Unix timestamp of expiry or null
   */
  getTokenExpiry() {
    const token = this.getToken()
    if (!token) return null
    
    const payload = parseJwt(token)
    return payload?.exp || null
  },

  /**
   * Get time until token expiry in seconds
   * @returns {number|null} Seconds until expiry or null if no token
   */
  getTimeUntilExpiry() {
    const expiry = this.getTokenExpiry()
    if (!expiry) return null
    
    const now = Math.floor(Date.now() / 1000)
    return expiry - now
  },

  /**
   * Get user info from token payload
   * @returns {Object|null} Decoded payload or null
   */
  getTokenPayload() {
    const token = this.getToken()
    if (!token) return null
    return parseJwt(token)
  },

  /**
   * Check if user has specific role from token
   * @param {string} role - Role to check
   * @returns {boolean} True if user has role
   */
  hasRole(role) {
    const payload = this.getTokenPayload()
    if (!payload) return false
    
    const roles = payload.roles || payload.role || []
    const roleArray = Array.isArray(roles) ? roles : [roles]
    return roleArray.includes(role)
  },

  /**
   * Get user ID from token
   * @returns {string|number|null} User ID or null
   */
  getUserId() {
    const payload = this.getTokenPayload()
    return payload?.sub || payload?.user_id || payload?.userid || payload?.id || null
  },

  /**
   * Validate token before API request
   * @returns {{valid: boolean, error: string|null, shouldRefresh: boolean}} Validation result
   */
  validateForRequest() {
    const token = this.getToken()
    
    if (!token) {
      return { valid: false, error: 'No token found', shouldRefresh: false }
    }
    
    if (!this.isTokenValid(token)) {
      return { valid: false, error: 'Token expired', shouldRefresh: !!this.getRefreshToken() }
    }
    
    const shouldRefresh = this.isTokenExpiringSoon(300) // 5 minutes
    return { valid: true, error: null, shouldRefresh }
  },

  /**
   * Debug helper - log token info (safe, no signature)
   */
  debug() {
    const token = this.getToken()
    const refresh = this.getRefreshToken()
    const payload = this.getTokenPayload()
    
    console.group('[TokenService Debug]')
    console.log('Has access token:', !!token)
    console.log('Has refresh token:', !!refresh)
    console.log('Token valid:', this.isTokenValid())
    console.log('Token expiring soon:', this.isTokenExpiringSoon())
    console.log('Time until expiry:', this.getTimeUntilExpiry(), 'seconds')
    console.log('Payload:', payload ? { 
      sub: payload.sub,
      exp: payload.exp,
      iat: payload.iat,
      roles: payload.roles || payload.role 
    } : null)
    console.groupEnd()
  }
}

export default tokenService
